import { useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import checkIcon from '@/assets/EventSection/check-icon.svg';
import eventImage from '@/assets/EventSection/event-image.webp';
import CustomButton from '@/components/Button/button';
import { useInView } from '@/hooks/useInView';
import { color } from '@/themes/color';
import { typography } from '@/themes/typography';
const PLANNING_FEATURES = [
  'Save vineyards you want to visit',
  'Add events to your plan',
  'Directions and route planner',
  'Invite friends to join',
] as const;

interface EventSectionProps {
  onDownloadClick: () => void;
}

export default function EventSection({
  onDownloadClick,
}: EventSectionProps): JSX.Element {
  const [sectionRef, inView] = useInView({
    threshold: 0.3,
    rootMargin: '0px 0px -80px 0px',
    unobserveOnce: true,
  });

  useEffect(() => {
    if (inView) {
      gtag('event', 'section_view', {
        event_category: 'Engagement',
        event_label: 'Event Section',
      });
    }
  }, [inView]);
  return (
    <Box
      ref={sectionRef}
      component="section"
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        height: { xs: 'auto', md: '100vh' },
      }}
    >
      <Box
        sx={{
          flex: { md: '0 0 50%' },
          width: { md: '50%' },
          minWidth: { md: 0 },
          minHeight: { xs: '40vh', md: 'auto' },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          boxSizing: 'border-box',
        }}
      >
        <Box
          component="img"
          src={eventImage}
          alt=""
          aria-hidden
          width={705}
          height={975}
          loading="lazy"
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
        />
      </Box>

      <Box
        sx={{
          flex: { md: '0 0 50%' },
          width: { md: '50%' },
          minWidth: { md: 0 },
          backgroundColor: color.merlot['400'],
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          p: { xs: '24px', md: '64px' },
          boxSizing: 'border-box',
        }}
      >
        <Box
          sx={{
            width: { xs: '100%', md: 'auto' },
            alignSelf: { xs: 'stretch', md: 'flex-start' },
          }}
        >
          <Typography
            component="h2"
            sx={{
              ...typography.display,
              display: 'block',
              color: color.cream['100'],
              lineHeight: 1.05,
              mb: '24px',
              textAlign: { xs: 'center', md: 'left' },
              fontSize: {
                xs: 'var(--font-size-36, 36px)',
                sm: 'var(--font-size-48, 48px)',
                md: typography.display.fontSize,
              },
            }}
          >
            Build your bespoke trip
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: '10px',
              mb: '28px',
              width: '100%',
            }}
          >
            {PLANNING_FEATURES.map((feature) => (
              <Box
                key={feature}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                }}
              >
                <Box component="img" src={checkIcon} alt="" aria-hidden />
                <Typography
                  sx={{
                    ...typography.heading5,
                    color: color.cream['100'],
                    fontWeight: 350,
                    lineHeight: 1.25,
                    fontSize: {
                      xs: 'var(--font-size-16, 16px)',
                      sm: 'var(--font-size-18, 18px)',
                      md: typography.heading5.fontSize,
                    },
                  }}
                >
                  {feature}
                </Typography>
              </Box>
            ))}
          </Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: { xs: 'center', md: 'flex-start' },
            }}
          >
            <CustomButton
              variant="contained"
              onClick={() => {
                gtag('event', 'cta_click', {
                  event_category: 'Engagement',
                  event_label: 'Start Planning – Event Section',
                });
                onDownloadClick();
              }}
              padding="14px 24px"
            >
              Start Planning
            </CustomButton>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
